import React, { useState } from 'react';
import { X, Upload } from 'lucide-react';
import { apiCall } from './invoiceApi';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Settings Modal
 * Modal for configuring invoice settings (company info, banking, numbering, footer, logo)
 */
const SettingsModal = ({ settings, language, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    company_name: settings.company_name || '', 
    address: settings.address || '',
    postal_code: settings.postal_code || '', 
    city: settings.city || '',
    vat_number: settings.vat_number || '', 
    email: settings.email || '',
    phone: settings.phone || '', 
    bank_name: settings.bank_name || '',
    iban: settings.iban || '', 
    bic: settings.bic || '',
    invoice_prefix: settings.invoice_prefix || 'FAC',
    credit_note_prefix: settings.credit_note_prefix || 'NC',
    default_due_days: settings.default_due_days || 30,
    invoice_footer_fr: settings.invoice_footer_fr || '',
    invoice_footer_nl: settings.invoice_footer_nl || '',
  });
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await apiCall('/api/invoicing/settings', { method: 'PUT', body: JSON.stringify(formData) });
      onSave();
    } catch (error) {
      alert(error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingLogo(true);
    try {
      const token = localStorage.getItem('ebb_token');
      const fd = new FormData();
      fd.append('file', file);
      await fetch(`${BACKEND_URL}/api/invoicing/settings/logo`, {
        method: 'POST', 
        headers: { 'Authorization': `Bearer ${token}` }, 
        body: fd,
      });
      alert(language === 'fr' ? 'Logo uploadé !' : 'Logo geüpload!');
    } catch (error) {
      alert(error.message);
    } finally {
      setUploadingLogo(false);
    }
  };

  const t = language === 'fr' ? {
    title: 'Paramètres', 
    company: 'Entreprise', 
    banking: 'Banque', 
    numbering: 'Numérotation',
    footer: 'Pied de page', 
    logo: 'Logo', 
    uploadLogo: 'Uploader', 
    save: 'Enregistrer', 
    cancel: 'Annuler',
  } : {
    title: 'Instellingen', 
    company: 'Bedrijf', 
    banking: 'Bank', 
    numbering: 'Nummering',
    footer: 'Voettekst', 
    logo: 'Logo', 
    uploadLogo: 'Uploaden', 
    save: 'Opslaan', 
    cancel: 'Annuleren',
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b flex justify-between items-center sticky top-0 bg-white">
          <h2 className="text-xl font-bold">{t.title}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Company */}
          <div>
            <h3 className="font-semibold mb-3">{t.company}</h3>
            <div className="grid grid-cols-2 gap-4">
              {['company_name', 'address', 'postal_code', 'city', 'vat_number', 'email', 'phone'].map((field) => (
                <input 
                  key={field} 
                  type="text" 
                  placeholder={field.replace('_', ' ')}
                  value={formData[field]} 
                  onChange={(e) => setFormData(prev => ({ ...prev, [field]: e.target.value }))}
                  className={`px-3 py-2 border rounded-lg ${field === 'address' || field === 'company_name' ? 'col-span-2' : ''}`} 
                />
              ))}
            </div>
          </div>
          
          {/* Banking */}
          <div>
            <h3 className="font-semibold mb-3">{t.banking}</h3>
            <div className="grid grid-cols-3 gap-4">
              {['bank_name', 'iban', 'bic'].map((field) => (
                <input 
                  key={field} 
                  type="text" 
                  placeholder={field.replace('_', ' ').toUpperCase()}
                  value={formData[field]} 
                  onChange={(e) => setFormData(prev => ({ ...prev, [field]: e.target.value }))}
                  className="px-3 py-2 border rounded-lg" 
                />
              ))}
            </div>
          </div>
          
          {/* Numbering */}
          <div>
            <h3 className="font-semibold mb-3">{t.numbering}</h3>
            <div className="grid grid-cols-3 gap-4">
              <input 
                type="text" 
                placeholder="Préfixe factures" 
                value={formData.invoice_prefix}
                onChange={(e) => setFormData(prev => ({ ...prev, invoice_prefix: e.target.value }))}
                className="px-3 py-2 border rounded-lg" 
              />
              <input 
                type="text" 
                placeholder="Préfixe NC" 
                value={formData.credit_note_prefix}
                onChange={(e) => setFormData(prev => ({ ...prev, credit_note_prefix: e.target.value }))}
                className="px-3 py-2 border rounded-lg" 
              />
              <input 
                type="number" 
                placeholder="Échéance" 
                value={formData.default_due_days}
                onChange={(e) => setFormData(prev => ({ ...prev, default_due_days: parseInt(e.target.value) || 30 }))}
                className="px-3 py-2 border rounded-lg" 
              />
            </div>
          </div>
          
          {/* Footer */}
          <div>
            <h3 className="font-semibold mb-3">{t.footer}</h3>
            <textarea 
              placeholder="Footer FR" 
              value={formData.invoice_footer_fr}
              onChange={(e) => setFormData(prev => ({ ...prev, invoice_footer_fr: e.target.value }))}
              rows={2} 
              className="w-full px-3 py-2 border rounded-lg mb-2" 
            />
            <textarea 
              placeholder="Footer NL" 
              value={formData.invoice_footer_nl}
              onChange={(e) => setFormData(prev => ({ ...prev, invoice_footer_nl: e.target.value }))}
              rows={2} 
              className="w-full px-3 py-2 border rounded-lg" 
            />
          </div>
          
          {/* Logo */}
          <div>
            <h3 className="font-semibold mb-3">{t.logo}</h3>
            <div className="flex items-center gap-4">
              {settings.logo_filename && (
                <img 
                  src={`${BACKEND_URL}/api/invoicing/logo/${settings.logo_filename}`} 
                  alt="Logo" 
                  className="h-16 object-contain" 
                />
              )}
              <label className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg cursor-pointer">
                <Upload className="w-4 h-4" />
                {uploadingLogo ? '...' : t.uploadLogo}
                <input 
                  type="file" 
                  accept="image/jpeg,image/png" 
                  onChange={handleLogoUpload} 
                  className="hidden" 
                />
              </label>
            </div>
          </div>
          
          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button 
              type="button" 
              onClick={onClose} 
              className="px-4 py-2 border rounded-lg"
            >
              {t.cancel}
            </button>
            <button 
              type="submit" 
              disabled={saving}
              className="px-4 py-2 bg-amber-700 hover:bg-amber-800 text-white rounded-lg font-medium"
            >
              {saving ? '...' : t.save}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SettingsModal;
